/**
 * BoardToolbar: search + filters + sort above a kanban board, using the exact
 * canonical toolbar markup the DataTable uses (.list-toolbar / .list-search /
 * .list-filter-toggle / .list-filters-inline / .list-filters-mobile /
 * .list-filter), so filters collapse correctly on mobile instead of clashing.
 * No inline styles. The owning page holds the state and does the actual
 * filtering/sorting of the rows it feeds to the board.
 */

import { useState } from 'react';
import type { Filter } from './DataTable';
import SearchSelect from './SearchSelect';

interface SortOption {
  value: string;
  label: string;
}

interface BoardToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  filters: Filter[];
  filterValues: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  sortOptions?: SortOption[];
  sortValue?: string;
  onSortChange?: (value: string) => void;
}

export default function BoardToolbar({
  search,
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters,
  filterValues,
  onFilterChange,
  sortOptions,
  sortValue,
  onSortChange,
}: BoardToolbarProps) {
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const activeFilterCount = filters.reduce(
    (n, f) => n + (filterValues[f.key] && filterValues[f.key] !== 'all' ? 1 : 0),
    0,
  );

  return (
    <>
      <div className="list-toolbar">
        <div className="list-toolbar-left">
          <div className="list-search">
            <span className="list-search-icon">🔍</span>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            {search && (
              <button className="list-search-clear" onClick={() => onSearchChange('')}>✕</button>
            )}
          </div>

          {filters.length > 0 && (
            <button className="list-filter-toggle" onClick={() => setFiltersExpanded(!filtersExpanded)}>
              <span>⚙</span>
              <span>Filters</span>
              {activeFilterCount > 0 && <span className="list-filter-badge">{activeFilterCount}</span>}
              <span className="list-filter-arrow">{filtersExpanded ? '▲' : '▼'}</span>
            </button>
          )}

          <div className="list-filters-inline">
            {filters.map((filter) => (
              <SearchSelect
                key={filter.key}
                width="inline"
                options={[
                  { value: 'all', label: `${filter.label}: All` },
                  ...filter.options.map((opt) => ({ value: opt.value, label: opt.label })),
                ]}
                value={filterValues[filter.key] || 'all'}
                onChange={(v) => onFilterChange(filter.key, v)}
              />
            ))}
          </div>
        </div>

        {sortOptions && sortOptions.length > 0 && onSortChange && (
          <div className="list-toolbar-right">
            <SearchSelect
              width="inline"
              searchable={false}
              options={sortOptions.map((o) => ({ value: o.value, label: o.label }))}
              value={sortValue ?? null}
              onChange={onSortChange}
            />
          </div>
        )}
      </div>

      {filters.length > 0 && (
        <div className={`list-filters-mobile ${filtersExpanded ? 'expanded' : ''}`}>
          {filters.map((filter) => (
            <SearchSelect
              key={filter.key}
              width="inline"
              options={[
                { value: 'all', label: `${filter.label}: All` },
                ...filter.options.map((opt) => ({ value: opt.value, label: opt.label })),
              ]}
              value={filterValues[filter.key] || 'all'}
              onChange={(v) => onFilterChange(filter.key, v)}
            />
          ))}
        </div>
      )}
    </>
  );
}
